import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Link } from "react-router";
import { Building2, Plus, Trash2, UserRound, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { AsideSection } from "../misc/AsideSection";
import type { Identifier } from "ra-core";

// LDF: Weitere Beteiligte am Auftrag mit Rolle (Caterer, Empfehlung, Ansprechpartner …).
// Auftraggeber, Endkunde und Location stehen fest am Auftrag; hier kommt alles Weitere dazu.

export const PARTICIPANT_ROLES: { value: string; label: string }[] = [
  { value: "ansprechpartner-auftraggeber", label: "Ansprechpartner Auftraggeber" },
  { value: "ansprechpartner-endkunde", label: "Ansprechpartner Endkunde" },
  { value: "ansprechpartner-location", label: "Ansprechpartner Location" },
  { value: "weiterer-einsatzort", label: "Weiterer Einsatzort" },
  { value: "empfehlung", label: "Empfehlung / Vermittlung" },
  { value: "caterer", label: "Caterer" },
  { value: "technik", label: "Technik / Licht / Ton" },
  { value: "fotograf", label: "Fotograf" },
  { value: "musik", label: "Musik / DJ" },
  { value: "moderation", label: "Moderation" },
  { value: "dienstleister", label: "Weiterer Dienstleister" },
  { value: "sonstiges", label: "Sonstiges" },
];

export const roleLabel = (value: string) => PARTICIPANT_ROLES.find((r) => r.value === value)?.label ?? value;

type Participant = {
  id: number;
  deal_id: number;
  role: string;
  note: string | null;
  company: { id: number; name: string } | null;
  contact: { id: number; first_name: string | null; last_name: string | null; title: string | null } | null;
  deal?: { id: number; name: string; event_date: string | null } | null;
};

const personName = (c: Participant["contact"]) => (c ? [c.first_name, c.last_name].filter(Boolean).join(" ") : "");

const SELECT = "id,deal_id,role,note,company:companies(id,name),contact:contacts(id,first_name,last_name,title)";

type Hit = { kind: "contact" | "company"; id: number; label: string; sub: string };

const useSearch = (term: string) => {
  const [hits, setHits] = useState<Hit[]>([]);
  useEffect(() => {
    const t = term.trim();
    if (t.length < 2) return setHits([]);
    const handle = setTimeout(async () => {
      const supabase = getSupabaseClient();
      const like = `%${t.replace(/[%,()]/g, " ")}%`;
      const [contacts, companies] = await Promise.all([
        supabase
          .from("contacts")
          .select("id,first_name,last_name,title,company:companies(name)")
          .or(`first_name.ilike.${like},last_name.ilike.${like}`)
          .limit(8),
        supabase.from("companies").select("id,name,sector").ilike("name", like).eq("contact_blocked", false).limit(6),
      ]);
      setHits([
        ...(contacts.data ?? []).map((c: any) => ({
          kind: "contact" as const,
          id: c.id,
          label: [c.first_name, c.last_name].filter(Boolean).join(" "),
          sub: [c.title, c.company?.name].filter(Boolean).join(" · "),
        })),
        ...(companies.data ?? []).map((c: any) => ({ kind: "company" as const, id: c.id, label: c.name, sub: "Firma" })),
      ]);
    }, 250);
    return () => clearTimeout(handle);
  }, [term]);
  return hits;
};

const AddParticipant = ({ dealId, onAdded }: { dealId: Identifier; onAdded: () => void }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [picked, setPicked] = useState<Hit | null>(null);
  const [role, setRole] = useState("caterer");
  const [note, setNote] = useState("");
  const hits = useSearch(picked ? "" : term);
  const notify = useNotify();

  const save = async () => {
    if (!picked) return;
    const { error } = await getSupabaseClient()
      .from("deal_participants")
      .insert({
        deal_id: dealId,
        role,
        note: note.trim() || null,
        contact_id: picked.kind === "contact" ? picked.id : null,
        company_id: picked.kind === "company" ? picked.id : null,
      });
    if (error) return notify(error.code === "23505" ? "Ist schon mit dieser Rolle eingetragen" : `Fehler: ${error.message}`, { type: "error" });
    setOpen(false);
    setTerm("");
    setPicked(null);
    setNote("");
    onAdded();
  };

  if (!open)
    return (
      <Button variant="outline" size="sm" className="gap-1" onClick={() => setOpen(true)}>
        <Plus className="h-3.5 w-3.5" /> Beteiligte hinzufügen
      </Button>
    );

  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <div className="relative">
        {picked ? (
          <div className="flex items-center gap-2 text-sm">
            {picked.kind === "contact" ? <UserRound className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
            <span className="font-medium">{picked.label}</span>
            <span className="text-xs text-muted-foreground">{picked.sub}</span>
            <button type="button" className="text-xs underline cursor-pointer" onClick={() => setPicked(null)}>
              ändern
            </button>
          </div>
        ) : (
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Person oder Firma suchen …"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          />
        )}
        {!picked && hits.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-md">
            {hits.map((h) => (
              <li key={`${h.kind}-${h.id}`}>
                <button type="button" onClick={() => setPicked(h)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted cursor-pointer">
                  {h.kind === "contact" ? <UserRound className="h-3.5 w-3.5 shrink-0" /> : <Building2 className="h-3.5 w-3.5 shrink-0" />}
                  <span className="truncate">{h.label}</span>
                  <span className="truncate text-xs text-muted-foreground">{h.sub}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          {PARTICIPANT_ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Wofür? (optional)"
          className="h-9 min-w-40 flex-1 rounded-md border border-input bg-background px-3 text-sm"
        />
        <Button size="sm" disabled={!picked} onClick={save}>
          Hinzufügen
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
};

export const DealParticipants = ({ dealId }: { dealId: Identifier }) => {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["deal-participants", dealId],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().from("deal_participants").select(SELECT).eq("deal_id", dealId).order("role");
      if (error) throw error;
      return (data ?? []) as unknown as Participant[];
    },
  });
  const reload = () => queryClient.invalidateQueries({ queryKey: ["deal-participants"] });

  const remove = async (id: number) => {
    await getSupabaseClient().from("deal_participants").delete().eq("id", id);
    reload();
  };

  return (
    <div className="m-4">
      <span className="flex items-center gap-1 text-xs text-muted-foreground tracking-wide">
        <Users className="h-3.5 w-3.5" /> Weitere Beteiligte
      </span>
      {data?.length ? (
        <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
          {data.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
              <span className="w-48 shrink-0 text-xs text-muted-foreground">{roleLabel(p.role)}</span>
              {p.contact && (
                <Link to={`/contacts/${p.contact.id}/show`} className="inline-flex items-center gap-1 font-medium">
                  <UserRound className="h-3.5 w-3.5" /> {personName(p.contact)}
                </Link>
              )}
              {p.company && (
                <Link to={`/companies/${p.company.id}/show`} className="inline-flex items-center gap-1 text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5" /> {p.company.name}
                </Link>
              )}
              {p.note && <span className="text-xs text-muted-foreground">– {p.note}</span>}
              <button type="button" onClick={() => remove(p.id)} className="ml-auto text-muted-foreground hover:text-destructive cursor-pointer" title="Entfernen">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">Noch keine – z. B. Caterer, Empfehlungsgeber oder Ansprechpartner der Location.</p>
      )}
      <div className="mt-2">
        <AddParticipant dealId={dealId} onAdded={reload} />
      </div>
    </div>
  );
};

// Bei Firma bzw. Kontakt: an welchen Aufträgen beteiligt und in welcher Rolle
export const ParticipationsAside = ({ companyId, contactId }: { companyId?: Identifier; contactId?: Identifier }) => {
  const { data } = useQuery({
    queryKey: ["participations", companyId, contactId],
    queryFn: async () => {
      let q = getSupabaseClient().from("deal_participants").select(`${SELECT},deal:deals(id,name,event_date)`);
      q = contactId ? q.eq("contact_id", contactId) : q.eq("company_id", companyId!);
      const { data } = await q;
      return ((data ?? []) as unknown as Participant[]).sort((a, b) => String(b.deal?.event_date ?? "").localeCompare(String(a.deal?.event_date ?? "")));
    },
  });
  if (!data?.length) return null;
  return (
    <AsideSection title="Beteiligt an Aufträgen">
      {data.map((p) => (
        <div key={p.id} className="text-sm">
          <Link to={`/deals/${p.deal_id}/show`} className="underline hover:no-underline">
            {p.deal?.name}
          </Link>
          <span className="block text-xs text-muted-foreground">
            {roleLabel(p.role)}
            {!contactId && p.contact ? ` · ${personName(p.contact)}` : ""}
            {p.note ? ` – ${p.note}` : ""}
          </span>
        </div>
      ))}
    </AsideSection>
  );
};
